import { useCallback, useEffect, useState } from "react";
import { Plus, Mail, Phone, AtSign, IdCard, HeartPulse, User } from "lucide-react";
import { Button } from "@/components/ui/button";
import { DriverDialog } from "@/components/DriverDialog";
import { PageHeader } from "@/pages/Events";
import { useSettings, fileUrl } from "@/context/SettingsContext";
import { useT } from "@/lib/i18n";
import { api } from "@/lib/api";
import { formatDate } from "@/lib/format";
import { driverName, medicalStatus, MEDICAL_STYLES } from "@/lib/drivers";

const DriverCard = ({ d, onOpen, index }) => {
  const { t, lang } = useT();
  const { settings } = useSettings();
  const status = medicalStatus(d.medical_expiry);
  return (
    <button type="button" data-testid={`driver-card-${d.id}`} onClick={() => onOpen(d)} style={{ animationDelay: `${Math.min(index, 10) * 40}ms` }}
      className="fade-up text-left bg-[#12141C] border border-[#282D3D] rounded-sm p-5 hover:border-[#FF3B30]/60 hover:bg-[#181B26] transition-colors duration-200 space-y-3">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          {d.photo ? (
            <img data-testid="driver-card-photo" src={fileUrl(d.photo.id)} alt={driverName(d)} className="w-14 h-14 rounded-sm object-cover border border-[#282D3D] shrink-0" />
          ) : (
            <div className="w-14 h-14 rounded-sm bg-[#090A0F] border border-[#282D3D] flex items-center justify-center shrink-0"><User className="w-6 h-6 text-slate-600" /></div>
          )}
        <div className="min-w-0">
          <h3 data-testid="driver-card-name" className="font-heading font-bold uppercase text-2xl leading-tight truncate">{driverName(d)}</h3>
          <div className="font-mono text-xs text-slate-500 mt-0.5">
            {d.birth_date ? formatDate(d.birth_date, lang) : ""}{!settings.hide_categories && d.category ? ` · ${d.category}` : ""}
          </div>
        </div>
        </div>
        {status && (
          <span data-testid="driver-card-medical" className={`shrink-0 flex items-center gap-1 border rounded-sm px-2 py-0.5 font-mono text-[10px] uppercase ${MEDICAL_STYLES[status]}`}>
            <HeartPulse className="w-3 h-3" />{t(`medical_${status}`)}
          </span>
        )}
      </div>
      <div className="space-y-1 font-mono text-xs text-slate-300">
        {d.email && <div className="flex items-center gap-2 truncate"><Mail className="w-3.5 h-3.5 text-slate-500" />{d.email}</div>}
        {d.phone && <div className="flex items-center gap-2"><Phone className="w-3.5 h-3.5 text-slate-500" />{d.phone}</div>}
        {d.social && <div className="flex items-center gap-2 truncate"><AtSign className="w-3.5 h-3.5 text-slate-500" />{d.social}</div>}
        {d.license_number && <div className="flex items-center gap-2"><IdCard className="w-3.5 h-3.5 text-slate-500" />{d.license_number}</div>}
      </div>
    </button>
  );
};

export default function Drivers() {
  const { t } = useT();
  const [drivers, setDrivers] = useState(null);
  const [dialog, setDialog] = useState({ open: false, driver: null });
  const load = useCallback(() => {
    api.get("/drivers").then((r) => setDrivers(r.data));
  }, []);
  useEffect(() => {
    load();
  }, [load]);

  return (
    <div>
      <PageHeader title={t("drivers_title")} sub={t("drivers_sub")} image="/images/helmet.jpg" imagePos="center 22%" action={
        <Button data-testid="new-driver-btn" onClick={() => setDialog({ open: true, driver: null })}
          className="rounded-sm bg-[#FF3B30] hover:bg-[#e0332a] font-heading uppercase tracking-wide text-lg h-11 px-5">
          <Plus className="w-5 h-5 mr-1" /> {t("new_driver")}
        </Button>
      } />
      {drivers && drivers.length === 0 && (
        <div data-testid="drivers-empty" className="border border-dashed border-[#282D3D] rounded-sm p-12 text-center text-slate-500 font-mono text-sm">{t("no_drivers")}</div>
      )}
      <div data-testid="drivers-list" className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {drivers?.map((d, i) => <DriverCard key={d.id} d={d} index={i} onOpen={(x) => setDialog({ open: true, driver: x })} />)}
      </div>
      <DriverDialog open={dialog.open} driver={dialog.driver} onSaved={load} onOpenChange={(o) => setDialog({ ...dialog, open: o })} />
    </div>
  );
}
